import { useCallback, useEffect, useState } from 'react';
import { AnalyticsEvent, AnalyticsEvents, EVENT_NAME } from '@belong/analytics';
import { logger } from '@belong/logging/logger';
import { waitForAdobe, waitForGtm } from './checkers';
import { sendToAdobe, sendToGtm } from './dispatchers';
import { makeWeakCancelable } from './utils';

const ADOBE_BUFFER_KEY = 'useConnectToAnalyticsVendors:adobe';
const GTM_BUFFER_KEY = 'useConnectToAnalyticsVendors:gtm';

/**
 * Send any analytics events to Adobe Target and Google Tag Manager.
 *
 * If any events fire before a vendor's script has loaded, they will be
 * "caught" and held in a queue until the vendor's platform has initialised.
 * Then all early caught events will be immediately re-dispatched to the
 * vendor in their original firing order.
 */
function useConnectToAnalyticsVendors(analyticsEvents: AnalyticsEvents, shouldRun: boolean): void {
  /*
   * Allow ourselves ot keep track of what's loaded and when
   */
  const [hasAdobeLoaded, setHasAdobeLoaded] = useState(false);
  const [hasGtmLoaded, setHasGtmLoaded] = useState(false);

  const onAdobeLoaded = useCallback((hasCanceled: boolean) => {
    if (!hasCanceled) {
      setHasAdobeLoaded(true);
    }
  }, []);

  const onGtmLoaded = useCallback((hasCanceled: boolean) => {
    if (!hasCanceled) {
      setHasGtmLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!shouldRun) {
      return;
    }

    /**
     * Setup listeners for our vendor scripts to know when we can stop collecting
     * the early events and start sending them directly to the vendor instead.
     */
    const { promise: gtmLoad, cancel: cancelGtmLoadWaiter } = makeWeakCancelable(waitForGtm());
    const { promise: adobeLoad, cancel: cancelAdobeLoadWaiter } = makeWeakCancelable(waitForAdobe());

    gtmLoad.then(onGtmLoaded).catch(err => logger.error('Gtm Load failed', err));
    adobeLoad.then(onAdobeLoaded).catch(err => logger.error('Adobe Load failed', err));

    /*
     * Cleanup on unmount
     */
    return (): void => {
      /*
       * Stop watching for the vendor script initialization
       * (note: this does not actually stop the vendor script loads)
       */
      cancelAdobeLoadWaiter();
      cancelGtmLoadWaiter();
    };
  }, [shouldRun, analyticsEvents, onAdobeLoaded, onGtmLoaded]);

  /*
   * When Adobe has loaded, make it subscribe to events
   * (and force-send all the events that happened before
   * it was ready)
   */
  useEffect(() => {
    if (hasAdobeLoaded) {
      analyticsEvents.addEventListener(EVENT_NAME, sendToAdobe);
      analyticsEvents.drainBufferedEvents(ADOBE_BUFFER_KEY).forEach((event: AnalyticsEvent) => {
        sendToAdobe(event);
      });
    }

    /*
     * Unsubscribe on cleanup.
     */
    return (): void => {
      analyticsEvents.removeEventListener(EVENT_NAME, sendToAdobe);
    };
  }, [hasAdobeLoaded, analyticsEvents]);

  /*
   * When GTM has loaded, make it subscribe to events
   * (and force-send all the events that happened before
   * it was ready)
   */
  useEffect(() => {
    if (hasGtmLoaded) {
      analyticsEvents.addEventListener(EVENT_NAME, sendToGtm);
      analyticsEvents.drainBufferedEvents(GTM_BUFFER_KEY).forEach((event: AnalyticsEvent) => {
        sendToGtm(event);
      });
    }

    /*
     * Unsubscribe on cleanup
     */
    return (): void => {
      analyticsEvents.removeEventListener(EVENT_NAME, sendToGtm);
    };
  }, [hasGtmLoaded, analyticsEvents]);

  /*
   * When all vendor scripts have loaded, the event bus no longer needs to
   * keep a replay buffer for late subscribers.
   */
  useEffect(() => {
    if (hasAdobeLoaded && hasGtmLoaded) {
      analyticsEvents.stopBufferingEvents();
    }
  }, [hasAdobeLoaded, hasGtmLoaded, analyticsEvents]);

  /*
   * Expose the `analyticsEvents` EventTarget class to the global
   * namespace so any other external vendors can subscribe on their
   * own without us having to hook them up.
   *
   * @todo: make Adobe and Google do this instead of us managing it here.
   */
  useEffect(() => {
    (window as any).BELONG = (window as any).BELONG ?? {};
    (window as any).BELONG.analyticsEvents = analyticsEvents;

    return (): void => {
      delete (window as any).BELONG.analyticsEvents;
    };
  }, [analyticsEvents]);
}

export default useConnectToAnalyticsVendors;
